import { Persistence } from "../config/constants";
import { BaseEntity } from "../core/entity/BaseEntity";
import { Entity } from "../core/entity/Entity";
import { on } from "../core/entity/handler";
import { deriveMachineCutLoad } from "../game/cut-load";
import { machineKey } from "../game/Machine";
import { floorMachines } from "../sim/entities/MachineEntity";
import {
  deriveMachineSoundPhase,
  MachineSoundPhase,
} from "../game/machine-sound-helpers";
import { MACHINE_VOICES } from "../utils/machineVoices";
import { MachineVoice } from "../utils/machineVoice";
import { projectPerson, projectProgression } from "../sim/projection";
import { Player } from "../sim/entities/Player";

/**
 * The shop's continuous machine voices. One-shots ride the `sound` event
 * (SoundView); machine hum can't, because operations pause, resume, and
 * survive a reload — a missed "stop" would leave a saw screaming
 * forever. So each placed machine gets a voice that converges on the
 * phase derived from the shop's own state, every tick.
 *
 * The view also keeps what each voice is audibly doing right now
 * (`audiblePhase`). The lead-in/lead-out sequencing runs on wall-clock
 * timers inside the voice, so the shop's state alone can't say whether
 * a machine is biting wood this frame or still spinning up; the machine
 * arts ask here so chips fly exactly while the cut is audible (see
 * `deriveMachineVisuals`). The voices report ahead of the draw, so the
 * art reads the phase the ear is hearing this frame.
 */
export class MachineSoundView extends BaseEntity implements Entity {
  persistenceLevel: number = Persistence.Permanent;
  pausable = false;

  private voices = new Map<string, MachineVoice>();
  /** The sequenced phase each voice was last handed, by machine key. */
  private phases = new Map<string, MachineSoundPhase>();

  /** What the machine with this key (type@x,y) sounds like right now. */
  audiblePhase(key: string): MachineSoundPhase {
    return this.phases.get(key) ?? "off";
  }

  // Once per frame is plenty: a machine's phase can't change faster
  // than the eye or ear can follow.
  @on("slowTick")
  onSlowTick() {
    if (!this.game.entities.tryGetSingleton(Player)) {
      this.silenceAll();
      return;
    }
    const person = projectPerson(this.game);
    const progression = projectProgression(this.game);
    const seen = new Set<string>();
    for (const machine of floorMachines(this.game)) {
      const makeVoice = MACHINE_VOICES[machine.state.machineTypeId];
      if (!makeVoice) continue;
      const key = machineKey(machine.state);
      seen.add(key);
      let voice = this.voices.get(key);
      if (!voice) {
        voice = makeVoice((phase) => this.phases.set(key, phase));
        this.voices.set(key, voice);
      }
      voice.setPhase(
        deriveMachineSoundPhase(
          machine,
          person.position,
          person.away !== null,
          person.operating === true,
          progression,
        ),
        deriveMachineCutLoad(machine),
      );
    }
    // Machines sold or picked up since the last look: silence them.
    for (const [key, voice] of this.voices) {
      if (!seen.has(key)) {
        voice.dispose();
        this.voices.delete(key);
        this.phases.delete(key);
      }
    }
  }

  /** Quitting to the menu takes the shop quiet with it. */
  private silenceAll(): void {
    if (this.voices.size === 0) return;
    this.voices.forEach((voice) => voice.dispose());
    this.voices.clear();
    this.phases.clear();
  }

  onDestroy() {
    this.silenceAll();
  }
}
